import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminStaffEditor() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/staff", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setStaff(res.data.content || []);
            } catch (error) {
                console.error("Error fetching staff:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchStaff();
    }, []);


    const handleChange = (index, field, value) => {
        const updated = [...staff];
        updated[index][field] = value;
        setStaff(updated);
    };

    const moveStaff = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= staff.length) return;

        const updated = [...staff];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setStaff(updated);
        setOpenIndex(newIndex);
    };

    const saveStaff = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/staff", { content: staff }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Staff saved!");
        } catch {
            toast.error("Failed to save staff");
        } finally {
            setSaving(false);
        }
    };

    const addStaff = () => {
        const newStaff = {
            name: "",
            role: "",
            image: "",
            email: "",
            phone: ""
        };
        setStaff([newStaff, ...staff]);
        setOpenIndex(0);
    };

    const deleteStaff = async (index) => {
        const staffMember = staff[index];
        const ok = await confirmSwal({
            title: `Delete staff ${staffMember.name || "(No Name)"}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = staff.filter((_, i) => i !== index);
        setStaff(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/staff", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Staff deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading({ ...uploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'staff');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange(index, 'image', response.data.url);
                toast.success('Image uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading({ ...uploading, [index]: false });
        }
    };

    if (loading) return <p>Loading staff...</p>;

    return (
        <div>
            {/* <h4>Featured Staff</h4> */}
            <p className="text-body-secondary">Staff members are displayed on the <em>Staff & Board</em> page.</p>

            <button className="btn btn-admin mb-3" onClick={addStaff}>+ Add Staff</button>

            <div className="accordion" id="projectsAccordion">
                {staff.map((staffMember, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`heading-${index}`}>
                            <button
                                className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(index)}
                            >
                                <strong>{staffMember.name || "(No Name)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Name</label>
                                    <input className="form-control" value={staffMember.name} onChange={(e) => handleChange(index, "name", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Role</label>
                                    <input className="form-control" value={staffMember.role} onChange={(e) => handleChange(index, "role", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Email <small className="text-body-secondary">(Optional)</small></label>
                                    <input 
                                        type="email" 
                                        className="form-control" 
                                        value={staffMember.email || ""} 
                                        onChange={(e) => handleChange(index, "email", e.target.value)}
                                        placeholder="email@example.com"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Phone <small className="text-body-secondary">(Optional)</small></label>
                                    <input 
                                        type="tel" 
                                        className="form-control" 
                                        value={staffMember.phone || ""} 
                                        onChange={(e) => handleChange(index, "phone", e.target.value)}
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Image</label>
                                    <input 
                                        className="form-control mb-2" 
                                        value={staffMember.image} 
                                        onChange={(e) => handleChange(index, "image", e.target.value)}
                                        placeholder="Image URL"
                                    />
                                    <div className="input-group">
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            accept=".jpg,.jpeg,.png,.heic"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleImageUpload(index, file);
                                            }}
                                            disabled={uploading[index]}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            disabled
                                        >
                                            {uploading[index] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {staffMember.image && (
                                        <div className="mt-2">
                                            <img 
                                                src={staffMember.image} 
                                                alt="Preview" 
                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveStaff(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveStaff(index, 1)}
                                            disabled={index === staff.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteStaff(index)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveStaff} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
